import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useDeepCompareEffect } from 'react-use';
import { useForm } from '@dotone/react-ui-core/hooks';
import {
  useCurrentUser,
  useShallowEqualSelector,
  useTranslate,
  useSearchSelector,
  useFilterSelector,
} from '@hooks';
import { createMarketplaceInvite, previewMarketplaceInvite, searchOffers } from '@actions';
import {
  FormGroup,
  FormBasic as Form,
  Input,
  Text,
  FormAction,
  Button,
  ButtonCancel,
  Spinner,
  Box,
  Alert,
} from '@dotone/react-ui-core';
import { EmailTemplateCheckboxGroup } from '../forms';
import MarketplaceInvitePreviewModal from './MarketplaceInvitePreviewModal';
import { EmailTemplateAlertModal } from '../EmailTemplates';

const MarketplaceInviteForm = ({ defaultValues, onCancel }) => {
  const { t, tHtml, locale } = useTranslate('marketplace', 'inviteModal');
  const dispatch = useDispatch();
  const { currentUser } = useCurrentUser();
  const [isOpen, setOpen] = useState(false);
  const [openedContentTemplates, setOpenedContentTemplates] = useState({});

  const { connectForm, handleSubmit, setError, getValues, clearErrors, watch, trigger, setValue } =
    useForm({
      defaultValues,
    });

  const [offerId, emailTemplateId, affiliateId] = watch([
    'offerId',
    'emailTemplateId',
    'affiliateId',
  ]);

  const { data: offers } = useSearchSelector(({ offerSearch }) => offerSearch, { ids: offerId });

  const { data: emailTemplates } = useFilterSelector(({ emailTemplateList }) => emailTemplateList, {
    locale,
    stateKey: [offerId, affiliateId].join('-'),
  });

  const offer = offers.find((o) => String(o.id) === String(offerId));
  const emailTemplate = emailTemplates.find((tem) => tem.id === emailTemplateId);

  const {
    isLoading: createLoading,
    status,
    data = { message: {} },
  } = useShallowEqualSelector(({ marketplaceInviteCreate }) => marketplaceInviteCreate);
  const {
    isLoading: previewLoading,
    status: previewStatus,
    data: previewData,
  } = useShallowEqualSelector(({ marketplaceInvitePreview }) => marketplaceInvitePreview);

  const isLoading = createLoading || previewLoading || !emailTemplates.length;

  const handleSubmitForm = (values) => {
    dispatch(createMarketplaceInvite(values));
  };

  const handlePreview = async () => {
    try {
      const isValid = await trigger();
      if (isValid) {
        const values = getValues();
        dispatch(previewMarketplaceInvite(values));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleToggle = () => {
    setOpen(!isOpen);
  };

  const handleAlertChange = (opened) => {
    setOpenedContentTemplates((prev) => ({ ...prev, [emailTemplateId]: opened }));
    if (opened) setValue('emailContent', emailTemplate.editableContent);
  };

  useEffect(() => {
    if (!offers.length && offerId) {
      dispatch(searchOffers({ ids: offerId }));
    }
  }, [dispatch, offers.length, offerId]);

  useEffect(() => {
    if (onCancel && status === 'success' && !isLoading) onCancel();
  }, [onCancel, status, isLoading]);

  useEffect(() => {
    if (isLoading) clearErrors();
  }, [isLoading, clearErrors]);

  useEffect(() => {
    clearErrors();
  }, [clearErrors, offerId]);

  useEffect(() => {
    if (previewStatus === 'success' && previewData.html) {
      setOpen(true);
    }
  }, [previewStatus, previewData.html]);

  useDeepCompareEffect(() => {
    if (status === 'error' && data.message) {
      Object.keys(data.message).forEach((key) =>
        setError(key, { type: 'custom', message: data.message[key] })
      );
    }
  }, [status, data.message, setError, defaultValues]);

  useDeepCompareEffect(() => {
    if (previewStatus === 'error' && previewData.message) {
      Object.keys(previewData.message).forEach((key) =>
        setError(key, { type: 'custom', message: previewData.message[key] })
      );
    }
  }, [previewStatus, previewData.message, setError, defaultValues]);

  return (
    <Form
      onSubmit={handleSubmit(handleSubmitForm)}
      onCancel={onCancel}
      submitting={isLoading}
      submitLabel={t('marketplace:actions.confirmSend')}
    >
      <div className="form-inner">
        {connectForm(
          <>
            <FormGroup>
              <Text>
                {tHtml('intro', {
                  offer: offer?.name,
                  remaining: currentUser.marketplaceInvitesAvailable,
                })}
              </Text>
            </FormGroup>

            <FormGroup className="mt-4">
              <EmailTemplateCheckboxGroup
                name="emailTemplateId"
                offerId={offerId}
                affiliateId={affiliateId}
                rules={{ required: true }}
              />
            </FormGroup>

            {!emailTemplates.length && <Spinner />}

            {emailTemplateId && (
              <>
                <Box label={t('subject')} className="mb-4 py-3 px-3">
                  <Text>{emailTemplate?.subject}</Text>
                </Box>

                {openedContentTemplates[emailTemplateId] ? (
                  <Box label={t('content')} className="p-3">
                    <Text html>{emailTemplate.layout[0]}</Text>
                    <Input type="html" name="emailContent" initProps={{ height: '400px' }} />
                    <Text html>{emailTemplate.layout[1]}</Text>
                  </Box>
                ) : (
                  <Box label={t('content')} className="py-3 px-3">
                    <EmailTemplateAlertModal onChange={handleAlertChange}>
                      <Text html className="overflow-auto" style={{ maxHeight: '60vh' }}>
                        {emailTemplate?.content}
                      </Text>
                    </EmailTemplateAlertModal>
                  </Box>
                )}
              </>
            )}

            {offerId && (
              <div className="text-center text-md">
                <Input type="hidden" name="offerId" rules={{ required: true }} />
              </div>
            )}
          </>
        )}
        {openedContentTemplates[emailTemplateId] && (
          <Alert variant="danger" className="font-italic mb-2 mt-3">
            {t('modifyReminder')}
          </Alert>
        )}
      </div>
      <MarketplaceInvitePreviewModal isOpen={isOpen} onToggle={handleToggle} data={previewData} />
      <FormAction align="right">
        <Button variant="preview" onClick={handlePreview} submitting={isLoading}>
          {t('common:actions.preview')}
        </Button>
        <Button type="submit" color="primary" submitting={isLoading} variant="send">
          {t('marketplace:actions.confirmSend')}
        </Button>
        {onCancel && <ButtonCancel disabled={isLoading} onClick={onCancel} />}
      </FormAction>
    </Form>
  );
};

export default MarketplaceInviteForm;
